import {
  editOpportunityStatuses,
  getStatusCounts,
  type StatusMigrationResult,
} from "@/api/deals-api";
import { Workspace } from "@/api/workspace-api";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { useDealsListStore } from "@/store/deals-list-store";
import { DragDropContext, Draggable, Droppable } from "@hello-pangea/dnd";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Edit2, GripVertical, Plus, Trash2 } from "lucide-react";
import { useState } from "react";
import { toast } from "sonner";
import posthog from "posthog-js";

interface WorkflowStatus {
  title: string;
  description: string;
  colour: string;
  order: number;
}

export default function WorkflowSettings({
  workspace,
}: {
  workspace: Workspace;
}) {
  const [isLoading, setIsLoading] = useState(false);
  const [statuses, setStatuses] = useState<WorkflowStatus[]>(
    workspace.statuses || []
  );

  const [isOpen, setIsOpen] = useState(false);
  const [isEditOpen, setIsEditOpen] = useState(false);
  const [editingStatus, setEditingStatus] = useState<WorkflowStatus | null>(
    null
  );
  const [editingOriginalTitle, setEditingOriginalTitle] = useState<string>("");
  const [editTitleError, setEditTitleError] = useState<string | null>(null);
  const [newStatus, setNewStatus] = useState({
    title: "",
    description: "",
    colour: "#3B82F6",
  });

  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [statusToDelete, setStatusToDelete] = useState<WorkflowStatus | null>(
    null
  );
  const [migrationTarget, setMigrationTarget] = useState<string>("");

  const { fetchDeals } = useDealsListStore();
  const queryClient = useQueryClient();

  const { data: statusCounts = {} } = useQuery({
    queryKey: ["status-counts", workspace._id],
    queryFn: () => getStatusCounts(workspace._id),
    staleTime: 30_000,
  });

  const sortedStatuses = [...statuses].sort((a, b) => a.order - b.order);
  const isLastStatus = statuses.length <= 1;

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (statuses.some((s) => s.title === newStatus.title)) {
      toast.error(`A status named "${newStatus.title}" already exists`);
      return;
    }
    const highestOrder = Math.max(...statuses.map((s) => s.order), 0);
    const newStatusWithOrder: WorkflowStatus = {
      ...newStatus,
      order: highestOrder + 10,
    };

    const next = [...statuses, newStatusWithOrder];
    setStatuses(next);
    setNewStatus({ title: "", description: "", colour: "#3B82F6" });
    setIsOpen(false);

    handleSaveStatuses(next, "added", newStatus.title);
  };

  const handleEditSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!editingStatus) return;

    const trimmedTitle = editingStatus.title.trim();
    const titleChanged = trimmedTitle !== editingOriginalTitle;

    if (
      titleChanged &&
      statuses.some(
        (s) => s.title === trimmedTitle && s.order !== editingStatus.order
      )
    ) {
      setEditTitleError(`A status named "${trimmedTitle}" already exists`);
      return;
    }
    setEditTitleError(null);

    const updatedStatuses = statuses.map((status) =>
      status.order === editingStatus.order
        ? { ...editingStatus, title: trimmedTitle }
        : status
    );

    setStatuses(updatedStatuses);
    setIsEditOpen(false);

    const migrations =
      titleChanged && (statusCounts[editingOriginalTitle] ?? 0) > 0
        ? { [editingOriginalTitle]: trimmedTitle }
        : undefined;

    handleSaveStatuses(updatedStatuses, "edited", trimmedTitle, migrations);
  };

  const handleDeleteClick = (status: WorkflowStatus) => {
    setStatusToDelete(status);
    const others = statuses.filter((s) => s.order !== status.order);
    setMigrationTarget(others[0]?.title ?? "");
    setDeleteConfirmOpen(true);
  };

  const confirmDelete = () => {
    if (!statusToDelete) return;
    const dealsOnStatus = statusCounts[statusToDelete.title] ?? 0;

    if (dealsOnStatus > 0 && !migrationTarget) {
      toast.error("Pick a status to move the deals to");
      return;
    }

    const updatedStatuses = statuses.filter(
      (status) => status.order !== statusToDelete.order
    );
    setStatuses(updatedStatuses);

    const migrations =
      dealsOnStatus > 0
        ? { [statusToDelete.title]: migrationTarget }
        : undefined;

    handleSaveStatuses(
      updatedStatuses,
      "deleted",
      statusToDelete.title,
      migrations
    );

    setDeleteConfirmOpen(false);
    setStatusToDelete(null);
    setMigrationTarget("");
  };

  const handleDragEnd = (result: {
    destination: { index: number } | null;
    source: { index: number };
  }) => {
    if (!result.destination) return;

    const items = Array.from(sortedStatuses);
    const [reorderedItem] = items.splice(result.source.index, 1);
    items.splice(result.destination.index, 0, reorderedItem);

    const updatedItems = items.map((item, index) => ({
      ...item,
      order: index * 10,
    }));

    setStatuses(updatedItems);
    handleSaveStatuses(updatedItems, "reordered");
  };

  const handleSaveStatuses = async (
    statusesToSave: WorkflowStatus[],
    action: "added" | "edited" | "deleted" | "reordered",
    statusName?: string,
    migrations?: Record<string, string>
  ) => {
    setIsLoading(true);
    try {
      const { migration_results } =
        await editOpportunityStatuses.editOpportunityStatuses(
          workspace._id,
          statusesToSave,
          migrations
        );

      posthog.capture("settings_workspace_statuses_updated", {
        workspace_id: workspace._id,
        action,
        status_name: statusName,
        total_statuses: statusesToSave.length,
        migrated_deals: migration_results.reduce(
          (sum, r) => sum + r.modified_count,
          0
        ),
      });

      toast.success(buildSaveToast(action, statusName, migration_results));

      queryClient.invalidateQueries({
        queryKey: ["status-counts", workspace._id],
      });
      queryClient.invalidateQueries({
        queryKey: ["workspace", workspace._id],
      });

      // Refresh kanban/table so migrated deals show up under the new status.
      if (migration_results.some((r) => r.modified_count > 0)) {
        await fetchDeals(workspace._id);
      }
    } catch (error) {
      console.error("Failed to save statuses:", error);
      toast.error("Failed to save workflow statuses");
    } finally {
      setIsLoading(false);
    }
  };

  const statusToDeleteCount = statusToDelete
    ? statusCounts[statusToDelete.title] ?? 0
    : 0;
  const deleteTargetOptions = statusToDelete
    ? sortedStatuses.filter((s) => s.order !== statusToDelete.order)
    : [];

  return (
    <div className="space-y-6 p-4">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h2 className="text-xl sm:text-2xl font-semibold tracking-tight">
            Statuses
          </h2>
          <p className="text-sm text-muted-foreground">
            Customize the statuses for tracking deals across your workflow.
          </p>
        </div>
        <div className="flex flex-col sm:flex-row gap-2">
          <Dialog open={isOpen} onOpenChange={setIsOpen}>
            <DialogTrigger asChild>
              <Button disabled={isLoading} className="w-full sm:w-auto">
                <Plus className="mr-2 h-4 w-4" />
                New status
              </Button>
            </DialogTrigger>
            <DialogContent className="w-[95vw] max-w-lg">
              <DialogHeader>
                <DialogTitle>New status</DialogTitle>
                <DialogDescription>
                  Add a new status to your workflow.
                </DialogDescription>
              </DialogHeader>
              <form onSubmit={handleSubmit} className="space-y-4">
                <div className="space-y-2">
                  <div className="flex items-center gap-2">
                    <input
                      type="color"
                      value={newStatus.colour}
                      onChange={(e) =>
                        setNewStatus({ ...newStatus, colour: e.target.value })
                      }
                      className="h-10 w-10 rounded border bg-background p-1"
                    />
                    <Input
                      placeholder="Status name..."
                      value={newStatus.title}
                      onChange={(e) =>
                        setNewStatus({ ...newStatus, title: e.target.value })
                      }
                      required
                    />
                  </div>
                </div>
                <div className="space-y-2">
                  <Textarea
                    placeholder="Enter description..."
                    value={newStatus.description}
                    onChange={(e) =>
                      setNewStatus({
                        ...newStatus,
                        description: e.target.value,
                      })
                    }
                    required
                  />
                </div>
                <div className="flex justify-end">
                  <Button type="submit" disabled={isLoading}>
                    Create
                  </Button>
                </div>
              </form>
            </DialogContent>
          </Dialog>
        </div>
      </div>

      <Dialog
        open={deleteConfirmOpen}
        onOpenChange={(open) => {
          if (!open) {
            setDeleteConfirmOpen(false);
            setStatusToDelete(null);
            setMigrationTarget("");
          }
        }}
      >
        <DialogContent className="w-[95vw] max-w-lg">
          <DialogHeader>
            <DialogTitle>Delete status</DialogTitle>
            <DialogDescription>
              {statusToDeleteCount > 0 ? (
                <>
                  <strong>{statusToDeleteCount}</strong>{" "}
                  {statusToDeleteCount === 1 ? "deal is" : "deals are"} on
                  &quot;{statusToDelete?.title}&quot;. Choose a status to move{" "}
                  {statusToDeleteCount === 1 ? "it" : "them"} to before
                  deleting.
                </>
              ) : (
                <>
                  Are you sure you want to delete the status &quot;
                  {statusToDelete?.title}&quot;? This action cannot be undone.
                </>
              )}
            </DialogDescription>
          </DialogHeader>

          {statusToDeleteCount > 0 && (
            <div className="space-y-2">
              <label className="text-sm font-medium">Move deals to</label>
              <Select
                value={migrationTarget}
                onValueChange={setMigrationTarget}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select a status..." />
                </SelectTrigger>
                <SelectContent>
                  {deleteTargetOptions.map((s) => (
                    <SelectItem key={s.order} value={s.title}>
                      {s.title}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          )}

          <DialogFooter className="flex gap-2 justify-end">
            <Button
              variant="outline"
              onClick={() => {
                setDeleteConfirmOpen(false);
                setStatusToDelete(null);
                setMigrationTarget("");
              }}
            >
              Cancel
            </Button>
            <Button
              variant="destructive"
              onClick={confirmDelete}
              disabled={
                isLoading || (statusToDeleteCount > 0 && !migrationTarget)
              }
            >
              {statusToDeleteCount > 0
                ? `Delete & move ${statusToDeleteCount} ${
                    statusToDeleteCount === 1 ? "deal" : "deals"
                  }`
                : "Delete"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <DragDropContext onDragEnd={handleDragEnd}>
        <Droppable droppableId="statuses">
          {(provided) => (
            <div
              {...provided.droppableProps}
              ref={provided.innerRef}
              className="space-y-2"
            >
              {sortedStatuses.map((status, index) => (
                <Draggable
                  key={status.order.toString()}
                  draggableId={status.order.toString()}
                  index={index}
                >
                  {(provided) => (
                    <div
                      ref={provided.innerRef}
                      {...provided.draggableProps}
                      className="flex flex-col sm:flex-row items-start sm:items-center gap-4 rounded-lg border bg-card p-4"
                    >
                      <div
                        {...provided.dragHandleProps}
                        className="flex items-center gap-2"
                      >
                        <GripVertical className="h-5 w-5 text-muted-foreground cursor-move" />
                        <div
                          className="h-3 w-3 rounded-full"
                          style={{ backgroundColor: status.colour }}
                        />
                      </div>
                      <div className="flex-1 space-y-1">
                        <p className="font-medium">{status.title}</p>
                        <p className="text-sm text-muted-foreground">
                          {status.description}
                        </p>
                      </div>
                      <div className="flex items-center gap-2 w-full sm:w-auto justify-end">
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => {
                            setEditingStatus(status);
                            setEditingOriginalTitle(status.title);
                            setEditTitleError(null);
                            setIsEditOpen(true);
                          }}
                        >
                          <Edit2 className="h-4 w-4" />
                        </Button>
                        {isLastStatus ? (
                          <TooltipProvider>
                            <Tooltip>
                              <TooltipTrigger asChild>
                                <span tabIndex={0}>
                                  <Button
                                    variant="ghost"
                                    size="sm"
                                    disabled
                                    aria-label="Cannot delete the last status"
                                  >
                                    <Trash2 className="h-4 w-4 text-muted-foreground" />
                                  </Button>
                                </span>
                              </TooltipTrigger>
                              <TooltipContent>
                                Cannot delete the last status
                              </TooltipContent>
                            </Tooltip>
                          </TooltipProvider>
                        ) : (
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleDeleteClick(status)}
                          >
                            <Trash2 className="h-4 w-4 text-destructive" />
                          </Button>
                        )}
                      </div>
                    </div>
                  )}
                </Draggable>
              ))}
              {provided.placeholder}
            </div>
          )}
        </Droppable>
      </DragDropContext>

      <Dialog
        open={isEditOpen}
        onOpenChange={(open) => {
          setIsEditOpen(open);
          if (!open) setEditTitleError(null);
        }}
      >
        <DialogContent className="w-[95vw] max-w-lg">
          <DialogHeader>
            <DialogTitle>Edit status</DialogTitle>
            <DialogDescription>
              Modify the existing status in your workflow.
            </DialogDescription>
          </DialogHeader>
          {editingStatus && (
            <form onSubmit={handleEditSubmit} className="space-y-4">
              <div className="space-y-2">
                <div className="flex items-center gap-2">
                  <input
                    type="color"
                    value={editingStatus.colour}
                    onChange={(e) =>
                      setEditingStatus({
                        ...editingStatus,
                        colour: e.target.value,
                      })
                    }
                    className="h-10 w-10 rounded border bg-background p-1"
                  />
                  <Input
                    placeholder="Status name..."
                    value={editingStatus.title}
                    onChange={(e) => {
                      setEditingStatus({
                        ...editingStatus,
                        title: e.target.value,
                      });
                      if (editTitleError) setEditTitleError(null);
                    }}
                    required
                  />
                </div>
                {editTitleError && (
                  <p className="text-xs text-destructive">{editTitleError}</p>
                )}
              </div>
              <div className="space-y-2">
                <Textarea
                  placeholder="Enter description..."
                  value={editingStatus.description}
                  onChange={(e) =>
                    setEditingStatus({
                      ...editingStatus,
                      description: e.target.value,
                    })
                  }
                  required
                />
              </div>
              <div className="flex justify-end">
                <Button type="submit" disabled={isLoading}>
                  Save Changes
                </Button>
              </div>
            </form>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}

function buildSaveToast(
  action: "added" | "edited" | "deleted" | "reordered",
  statusName: string | undefined,
  migrations: StatusMigrationResult[]
): string {
  const migrated = migrations.reduce((sum, r) => sum + r.modified_count, 0);

  if (action === "deleted" && migrated > 0 && migrations[0]) {
    return `Deleted "${migrations[0].from}" — ${migrated} ${
      migrated === 1 ? "deal" : "deals"
    } moved to "${migrations[0].to}"`;
  }
  if (action === "edited" && migrated > 0 && migrations[0]) {
    return `Renamed to "${migrations[0].to}" — ${migrated} ${
      migrated === 1 ? "deal" : "deals"
    } updated`;
  }
  if (action === "deleted" && statusName) {
    return `Deleted "${statusName}"`;
  }
  if (action === "added" && statusName) {
    return `Added "${statusName}"`;
  }
  if (action === "edited" && statusName) {
    return `Updated "${statusName}"`;
  }
  return "Workflow statuses have been updated";
}
